import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronRight, Check, ListChecks, X } from "lucide-react";
import { type PermissionGroup, LABEL_CONFIG, getChildGroups, getRootGroups, getGroupPath } from "./types";
import { HierarchyMultiPickerDialog } from "./HierarchyMultiPickerDialog";

// --- Tree Node ---

type TreeNodeProps = {
  group: PermissionGroup;
  groups: PermissionGroup[];
  selectedId: string | null;
  expandedIds: Set<string>;
  level: number;
  onSelect: (group: PermissionGroup) => void;
  onToggleExpand: (groupId: string) => void;
};

const TreeNode = ({ group, groups, selectedId, expandedIds, level, onSelect, onToggleExpand }: TreeNodeProps) => {
  const children = getChildGroups(groups, group.id);
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(group.id);
  const isSelected = selectedId === group.id;
  const config = LABEL_CONFIG[group.label];
  const Icon = config.icon;

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(group)}
        className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm rounded transition-colors ${
          isSelected ? "bg-zinc-700 text-white" : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
        }`}
        style={{ paddingLeft: level * 16 + 8 }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <span
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(group.id);
            }}
            className="p-0.5 -ml-1 text-zinc-500 hover:text-white"
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        ) : (
          <span className="w-5" />
        )}

        {/* Icon */}
        <div className={`w-5 h-5 flex items-center justify-center rounded ${config.bgColor}`}>
          <Icon size={12} className={config.color} />
        </div>

        {/* Name */}
        <span className="flex-1 truncate">{group.name}</span>

        {/* Selected check */}
        {isSelected && <Check size={14} className="text-blue-400" />}
      </button>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.id}
              group={child}
              groups={groups}
              selectedId={selectedId}
              expandedIds={expandedIds}
              level={level + 1}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- Main Picker ---

type CommonProps = {
  groups: PermissionGroup[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

type SingleProps = CommonProps & {
  allowMultiple?: false;
  selectedId: string | null;
  onChange: (groupId: string | null) => void;
};

type MultipleProps = CommonProps & {
  allowMultiple: true;
  selectedIds: string[];
  onChange: (groupIds: string[]) => void;
};

export type HierarchyPickerProps = SingleProps | MultipleProps;

export const HierarchyPicker = (props: HierarchyPickerProps) => {
  const { groups, placeholder = "Select target...", disabled, className = "" } = props;
  const allowMultiple = props.allowMultiple === true;

  // The picker still works with a "primary" id for the inline dropdown UI.
  // In multi-mode the primary is the first selected id (if any).
  const primaryId = allowMultiple ? (props.selectedIds[0] ?? null) : props.selectedId;

  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showMultiDialog, setShowMultiDialog] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const rootGroups = getRootGroups(groups);

  // Auto-expand path to selected group when opening
  useEffect(() => {
    if (isOpen && primaryId) {
      const path = getGroupPath(groups, primaryId);
      setExpandedIds(new Set(path.map((g) => g.id)));
    }
  }, [isOpen, primaryId, groups]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleExpand = (groupId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const handleSelect = (group: PermissionGroup) => {
    if (props.allowMultiple === true) {
      // Inline pick: replace selection with this one id. Multi-select is via the dialog.
      props.onChange([group.id]);
    } else {
      props.onChange(group.id);
    }
    setIsOpen(false);
  };

  // --- Trigger display ---

  const selectedCount = allowMultiple ? props.selectedIds.length : primaryId ? 1 : 0;
  const isMultiSelected = allowMultiple && selectedCount > 1;
  const primaryGroup = primaryId ? (groups.find((g) => g.id === primaryId) ?? null) : null;

  const displayText = isMultiSelected ? "Multiple selected" : (primaryGroup?.name ?? null);

  const selectedGroups = allowMultiple
    ? (props as MultipleProps).selectedIds
        .map((id) => groups.find((g) => g.id === id))
        .filter((g): g is PermissionGroup => g !== undefined)
    : [];

  const fullPathText = (() => {
    if (allowMultiple && props.allowMultiple === true) {
      if (props.selectedIds.length === 0) return null;
      return props.selectedIds
        .map((id) =>
          getGroupPath(groups, id)
            .map((g) => g.name)
            .join(" → "),
        )
        .join("\n");
    }
    return primaryGroup
      ? getGroupPath(groups, primaryGroup.id)
          .map((g) => g.name)
          .join(" → ")
      : null;
  })();

  const config = primaryGroup ? LABEL_CONFIG[primaryGroup.label] : null;
  const Icon = config?.icon;
  const showIcon = !!Icon && !!config && selectedCount === 1;

  const handleTriggerClick = () => {
    if (disabled) return;
    // In multi-mode with >1 selected, the inline tree is unhelpful — go straight to the dialog.
    if (isMultiSelected) {
      setShowMultiDialog(true);
      return;
    }
    setIsOpen(!isOpen);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleTriggerClick}
        disabled={disabled}
        title={fullPathText ?? undefined}
        className={`w-full flex items-center gap-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-left text-sm transition-colors ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-zinc-600"
        } ${isOpen ? "border-blue-500" : ""}`}
      >
        {showIcon && Icon && config && (
          <div className={`w-5 h-5 flex items-center justify-center rounded ${config.bgColor}`}>
            <Icon size={12} className={config.color} />
          </div>
        )}
        <span className={`flex-1 truncate ${displayText ? "text-white" : "text-zinc-500"}`}>
          {displayText || placeholder}
        </span>
        <ChevronDown size={16} className={`text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Selected rows list (multi-mode, >1 selected) */}
      {isMultiSelected && (
        <div className="mt-2 space-y-1">
          {selectedGroups.map((g) => {
            const rowConfig = LABEL_CONFIG[g.label];
            const RowIcon = rowConfig.icon;
            const path = getGroupPath(groups, g.id)
              .map((p) => p.name)
              .join(" → ");
            return (
              <div
                key={g.id}
                className="flex items-center gap-2 px-2 py-1.5 bg-zinc-800/60 border border-zinc-800 rounded text-sm text-zinc-200"
                title={path}
              >
                <div className={`w-5 h-5 flex items-center justify-center rounded shrink-0 ${rowConfig.bgColor}`}>
                  <RowIcon size={12} className={rowConfig.color} />
                </div>
                <span className="flex-1 truncate">{g.name}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (props.allowMultiple === true) {
                      props.onChange(props.selectedIds.filter((id) => id !== g.id));
                    }
                  }}
                  className="p-0.5 text-zinc-500 hover:text-white"
                  aria-label={`Remove ${g.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-h-72 overflow-hidden flex flex-col">
          {/* Header (only in multi-allowed mode) */}
          {allowMultiple && (
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-zinc-800">
              <span className="text-xs text-zinc-500 px-1">Pick one</span>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setShowMultiDialog(true);
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-zinc-800 rounded"
              >
                <ListChecks size={12} />
                Select multiple
              </button>
            </div>
          )}

          <div className="p-1 overflow-auto">
            {rootGroups.map((group) => (
              <TreeNode
                key={group.id}
                group={group}
                groups={groups}
                selectedId={primaryId}
                expandedIds={expandedIds}
                level={0}
                onSelect={handleSelect}
                onToggleExpand={toggleExpand}
              />
            ))}
          </div>
        </div>
      )}

      {/* Multi-select dialog */}
      {allowMultiple && props.allowMultiple === true && showMultiDialog && (
        <HierarchyMultiPickerDialog
          groups={groups}
          initialSelectedIds={props.selectedIds}
          onClose={() => setShowMultiDialog(false)}
          onConfirm={(ids) => {
            props.onChange(ids);
            setShowMultiDialog(false);
          }}
        />
      )}
    </div>
  );
};

export default HierarchyPicker;
